import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import {
  cancelCurrentUserQueries,
  useQueryUser,
  useUpdateUser,
} from '@heidi/workspace/data';
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback, useEffect, type SetStateAction } from 'react';
import { useIntl } from 'react-intl';

/**
 * Readable width is on by default. Local storage is a best-effort cache for
 * older backends/frontends; a supported GET /user is authoritative. In
 * particular, we deliberately do not migrate a legacy local-only choice to
 * the backend. A one-time width change on rollout is preferable to maintaining
 * a second source of truth and a migration protocol for a visual preference.
 */
const warnedStorageErrors = new Set<string>();

export const canvasReadableWidthAtom = atomWithStorage<boolean>(
  'heidi-canvas-readable-width',
  true,
  createJSONStorage<boolean>(() =>
    getSafeLocalStorage((operation, key, error) => {
      const name = error instanceof Error ? error.name : 'UnknownError';
      const failure = `${operation}:${key}:${name}`;
      if (warnedStorageErrors.has(failure)) return;
      warnedStorageErrors.add(failure);
      logger.warn(
        `[readableWidth] localStorage ${operation} failed for ${key} (${name})`
      );
    })
  ),
  { getOnInit: true }
);

const canvasReadableWidthSavingAtom = atom(false);
const canvasReadableWidthLastRemoteAtom = atom<{
  userId: string;
  value: boolean;
} | null>(null);

const selectReadableWidth = (user: {
  user_ref_id: string;
  session_settings?: { is_readable_width_enabled?: boolean | null } | null;
}) => ({
  userId: user.user_ref_id,
  isSupported: Boolean(
    user.session_settings &&
    Object.prototype.hasOwnProperty.call(
      user.session_settings,
      'is_readable_width_enabled'
    )
  ),
  value: user.session_settings?.is_readable_width_enabled,
});

/** Mount once on the V2 session page or inside the V2 Display Controls row. */
export const useSyncCanvasReadableWidthPreference = (): void => {
  const { data: remote } = useQueryUser(selectReadableWidth);
  const store = useStore();
  const setLocalPreference = useSetAtom(canvasReadableWidthAtom);
  const isSaving = useAtomValue(canvasReadableWidthSavingAtom);
  const userId = remote?.userId;
  const value = remote?.value;
  const isSupported = remote?.isSupported;

  useEffect(() => {
    if (!isSupported || !userId || typeof value !== 'boolean' || isSaving) return;

    const last = store.get(canvasReadableWidthLastRemoteAtom);
    // A local toggle is not a new backend value. Only a
    // changed GET /user value or user identity may hydrate the local cache.
    if (last?.userId === userId && last.value === value) return;
    store.set(canvasReadableWidthLastRemoteAtom, { userId, value });
    setLocalPreference(value);
  }, [isSaving, isSupported, setLocalPreference, store, userId, value]);
};

/**
 * A supported backend persists explicit choices. While GET /user is unknown,
 * controls are disabled rather than accepting a choice that may be overwritten
 * moments later. A rolled-back backend omits the field and stays local-only.
 */
export const useCanvasReadableWidthPreference = (): readonly [
  boolean,
  (next: SetStateAction<boolean>) => void,
  boolean,
] => {
  const intl = useIntl();
  const store = useStore();
  const [localPreference, setLocalPreference] = useAtom(canvasReadableWidthAtom);
  const isSaving = useAtomValue(canvasReadableWidthSavingAtom);
  const { data: remote, isPending: isLoadingUser } = useQueryUser(selectReadableWidth);
  const { mutateAsync: updateUser } = useUpdateUser({ enableInvalidation: false });

  const setPreference = useCallback(
    (next: SetStateAction<boolean>) => {
      if (isLoadingUser || store.get(canvasReadableWidthSavingAtom)) return;

      const previous = store.get(canvasReadableWidthAtom);
      const value = typeof next === 'function' ? next(previous) : next;
      setLocalPreference(value);
      if (!remote?.isSupported) return;

      store.set(canvasReadableWidthSavingAtom, true);
      // An older GET /user must not land after the optimistic PATCH and
      // replace the just-selected value in the shared query cache.
      void cancelCurrentUserQueries()
        .then(() =>
          updateUser({ session_settings: { is_readable_width_enabled: value } })
        )
        .then(
          () => store.set(canvasReadableWidthSavingAtom, false),
          () => {
            // Core useUpdateUser invalidates GET /user on error. Its refetch will
            // restore the authoritative value; never roll back from a stale GET.
            store.set(canvasReadableWidthLastRemoteAtom, null);
            store.set(canvasReadableWidthSavingAtom, false);
            toast({
              tone: 'destructive',
              title: intl.formatMessage({
                defaultMessage: 'Could not save readable width. Please try again.',
                id: 'pXYPI7MwNq',
              }),
            });
          }
        );
    },
    [isLoadingUser, intl, remote?.isSupported, setLocalPreference, store, updateUser]
  );

  return [localPreference, setPreference, isLoadingUser || isSaving] as const;
};

/** Canvas layout uses full-width tokens, the inverse of readable width. */
export const useIsCanvasFullWidth = (): boolean => !useAtomValue(canvasReadableWidthAtom);
